import { AppHeader, AppLoader, LoginView, TabView, getIcon } from './lib/index.js';
import { ProjectStore, CandidateStore, PromptStore, AnalysisQueueStore, EmailTemplateStore, EmailStore } from './stores/index.js';
import { resolveTenant, tenantPrefix } from './lib/tenant.js';
import { ResumeToolbar } from './ui/ResumeToolbar.js';
import { ProjectManager } from './ui/ProjectManager.js';
import { CandidateTable } from './ui/CandidateTable.js';
import { PromptEditor } from './ui/PromptEditor.js';
import { ProjectConfigPanel } from './ui/ProjectConfigPanel.js';
import { EmailTemplateEditor } from './ui/EmailTemplateEditor.js';
import { HowToPanel } from './ui/HowToPanel.js';
import { ProjectDashboard } from './ui/ProjectDashboard.js';
import { UserManager } from './ui/UserManager.js';
import './styles/main.css';
import './styles/toolbar.css';
import './styles/resumemanager.css';

const APP_VERSION = '1.0.0';

export class RavenApp {
    constructor() {
        this.db = null;
        this.auth = null;
        this.loader = null;

        this._user = null;
        this._userData = null;
        this._userRole = null;

        this.header = null;
        this.loginView = null;

        this.toolbar = null;
        this.tabView = null;
        this.projectManager = null;
        this.candidateTable = null;
        this.promptEditor = null;
        this.emailTemplateEditor = null;
        this.projectConfig = null;
        this.howToPanel = null;
        this.dashboard = null;
        this.userManager = null;

        this._tabPanels = {};

        this.projectStore = null;
        this.candidateStore = null;
        this.promptStore = null;
        this.emailTemplateStore = null;
        this.emailStore = null;
        this.analysisQueue = null;

        this._activeProjectId = null;
        this._columnMenuVisible = false;
        this._hideRejected = false;

        this._tenant = null;
        this._prefix = '';
    }

    async init() {
        this.loader = new AppLoader();
        this.loader.setStatus('Initializing');

        // Resolve tenant from subdomain or URL param
        this._tenant = resolveTenant();
        this._prefix = tenantPrefix(this._tenant);

        this.db = firebase.firestore();
        this.auth = firebase.auth();

        this.loader.setStatus('Authenticating');

        this.header = new AppHeader({
            appName: 'Resume',
            version: APP_VERSION,
            onSignOut: () => this._signOut(),
            onThemeChange: (theme) => {
                localStorage.setItem('raven-theme', theme);
            },
        });

        this.loginView = new LoginView({
            title: 'Raven Resume',
            subtitle: 'AI-powered resume screening and management',
            onSendLink: (email) => this._sendSignInLink(email),
            onCompleteSignIn: null, // handled below after auth listener is ready
            onRequestAccess: (email, name, reason) => this._requestAccess(email, name, reason),
        });
        this.loginView.hide();

        this._setupAuthListener();
    }

    // ========== AUTH ==========

    _setupAuthListener() {
        // Set up the auth state listener FIRST
        this.auth.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    this.loader.setStatus('Checking access');
                    const userData = await this._checkAccess(user.email);
                    if (!userData) {
                        this.loginView.showError('Access denied. Contact your administrator.');
                        await this.auth.signOut();
                        this.loader.hide();
                        return;
                    }
                    this._user = user;
                    this._userData = userData;
                    this._userRole = userData.role || 'user';
                    await this._showApp();
                    this._startSessionRefresh();
                } catch (err) {
                    console.error('Auth check failed:', err);
                    this.loginView.showError('Error: ' + err.message);
                    this.loader.hide();
                }
            } else {
                this._dispose();
                this._showLogin();
            }
        });

        // Now check for magic link token in URL, or try session restore
        const hash = window.location.hash;
        if (hash.includes('token=')) {
            this._completeSignIn();
        } else {
            this._trySessionRestore();
        }
    }

    /** Try to restore session from stored session token */
    async _trySessionRestore() {
        const sessionToken = localStorage.getItem('raven-session');
        if (!sessionToken) return;

        try {
            this.loader.setStatus('Restoring session');
            const refreshSession = firebase.functions().httpsCallable('refreshSession');
            const result = await refreshSession({ sessionToken });
            const { customToken, sessionToken: newToken, sessionExpiresAt } = result.data;

            // Store new rotated token
            localStorage.setItem('raven-session', newToken);
            localStorage.setItem('raven-session-expires', sessionExpiresAt);

            // Sign into Firebase with the custom token — triggers onAuthStateChanged
            await this.auth.signInWithCustomToken(customToken);
        } catch (err) {
            console.log('Session restore failed:', err.message);
            localStorage.removeItem('raven-session');
            localStorage.removeItem('raven-session-expires');
            // Show login since onAuthStateChanged won't fire again
            this._showLogin();
        }
    }

    /** Refresh session every 6 hours to rotate the cookie */
    _startSessionRefresh() {
        if (this._refreshInterval) clearInterval(this._refreshInterval);
        this._refreshInterval = setInterval(() => {
            this._trySessionRestore().catch(() => {});
        }, 6 * 60 * 60 * 1000); // every 6 hours
    }

    async _checkAccess(email) {
        const doc = await this.db.collection('app-users').doc(email).get();
        if (!doc.exists) return null;
        const data = doc.data();
        if (!data.role || !['admin', 'user'].includes(data.role)) return null;
        return data;
    }

    async _sendSignInLink(email) {
        try {
            const sendMagicLink = firebase.functions().httpsCallable('sendMagicLink');
            await sendMagicLink({ email: email.toLowerCase().trim() });
        } catch (err) {
            const msg = err.message || '';
            if (msg.includes('not-found') || msg.includes('No account')) {
                this.loginView.showError('No account found for this email. Use "Request Access" below.');
            } else {
                this.loginView.showError(msg);
            }
            throw err;
        }
    }

    async _completeSignIn() {
        // Check URL hash for magic link token
        const hash = window.location.hash;
        const tokenMatch = hash.match(/token=([a-f0-9]+)/);
        if (!tokenMatch) return;

        const token = tokenMatch[1];
        this.loader.setStatus('Signing in');

        try {
            const verifyMagicLink = firebase.functions().httpsCallable('verifyMagicLink');
            const result = await verifyMagicLink({ token });
            const { customToken, sessionToken, sessionExpiresAt } = result.data;

            // Store session
            localStorage.setItem('raven-session', sessionToken);
            localStorage.setItem('raven-session-expires', sessionExpiresAt);

            // Sign into Firebase
            await this.auth.signInWithCustomToken(customToken);

            // Clean URL
            window.history.replaceState(null, '', window.location.pathname);
        } catch (err) {
            const code = err.code || '';
            const msg = err.message || '';

            // Clean URL
            window.history.replaceState(null, '', window.location.pathname);

            if (code.includes('deadline-exceeded') || msg.includes('expired')) {
                // Link expired — show login with helpful message
                this.loginView.showError('This sign-in link has expired. Enter your email to get a new one.');
            } else if (code.includes('already-exists') || msg.includes('already been used')) {
                this.loginView.showError('This sign-in link has already been used. Enter your email to get a new one.');
            } else {
                this.loginView.showError('Sign-in failed: ' + msg);
            }
            this.loader.hide();
        }
    }

    async _requestAccess(email, name, reason) {
        try {
            await this.db.collection('access-requests').add({
                email: email.toLowerCase().trim(),
                name,
                reason,
                status: 'pending',
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            });
        } catch (err) {
            this.loginView.showError(err.message);
            throw err;
        }
    }

    async _signOut() {
        // Clear session
        const sessionToken = localStorage.getItem('raven-session');
        localStorage.removeItem('raven-session');
        localStorage.removeItem('raven-session-expires');
        if (this._refreshInterval) clearInterval(this._refreshInterval);

        this._dispose();
        await this.auth.signOut();
    }

    _showLogin() {
        document.getElementById('mainApp').style.display = 'none';
        this.loginView.show();
        this.loader.hide();
    }

    // ========== APP DISPLAY ==========

    async _showApp() {
        this.loginView.hide();
        const mainEl = document.getElementById('mainApp');
        mainEl.style.display = 'block';

        const roleLabel = this._userRole === 'admin' ? 'Admin' : 'User';
        this.header.setUser(this._user.email, this._userRole, roleLabel);

        // Show tenant name in header if not default
        if (this._tenant && this._tenant !== 'default') {
            this.header.setTenantBadge(this._tenant);
        }

        const savedTheme = localStorage.getItem('raven-theme');
        if (savedTheme) {
            this.header.applyThemePreference(savedTheme);
        }

        this.loader.setStatus('Loading data');

        // Create stores
        this.projectStore = new ProjectStore(this.db);
        this.candidateStore = new CandidateStore(this.db);
        this.promptStore = new PromptStore(this.db);
        this.analysisQueue = new AnalysisQueueStore(this.db);
        this.emailTemplateStore = new EmailTemplateStore(this.db);
        this.emailStore = new EmailStore(this.db);

        // Wire up listeners
        this.projectStore.onChange((projects) => this._onProjectsChanged(projects));
        this.candidateStore.onChange((candidates) => this._onCandidatesChanged(candidates));
        this.candidateStore.onError((err) => {
            this.toolbar?.statusBar?.log(`Firestore error: ${err.message}`, 'error');
        });
        this.promptStore.onChange((prompts) => this._onPromptsChanged(prompts));
        this.emailTemplateStore.onChange((templates) => {
            if (this.emailTemplateEditor) this.emailTemplateEditor.setTemplates(templates);
        });
        this.analysisQueue.onProgress((jobs) => this._onJobsChanged(jobs));

        // Build UI
        this._buildUI();

        // Subscribe to data
        this.projectStore.subscribeAll();
        this.promptStore.subscribeAll();
        this.emailTemplateStore.subscribeAll();
        this.loader.hide();

        this._initProjectFromUrl();
    }

    // ========== UI BUILDING ==========

    _buildUI() {
        this.toolbar = new ResumeToolbar(
            document.getElementById('toolbarContainer'),
            { onAction: (id) => this._handleToolbarAction(id) }
        );

        this.tabView = new TabView(document.getElementById('contentArea'), {
            onTabChange: (tabId) => this._onTabChange(tabId),
        });

        // Dashboard tab (first, default)
        this.dashboard = new ProjectDashboard({
            db: this.db,
            projectStore: this.projectStore,
            candidateStore: this.candidateStore,
            promptStore: this.promptStore,
            statusBar: this.toolbar?.statusBar,
            onSelectCandidate: (id) => {
                // Could scroll to / highlight candidate in table
            },
            onSwitchTab: (tabId) => {
                this.tabView.setActiveTab(tabId);
            },
        });
        this._tabPanels.dashboard = this.dashboard.element;
        this.tabView.addTab('dashboard', 'Dashboard', this._tabPanels.dashboard);

        // Candidate Table
        this.candidateTable = new CandidateTable(null, {
            candidateStore: this.candidateStore,
            promptStore: this.promptStore,
            emailStore: this.emailStore,
            onDispositionChange: (id, val) => {
                this.candidateStore.update(id, { disposition: val }).catch(err => {
                    this.toolbar.statusBar.log(`Save failed: ${err.message}`, 'error');
                });
            },
            onFlagChange: (id, field, val) => {
                this.candidateStore.update(id, { [field]: val }).catch(err => {
                    this.toolbar.statusBar.log(`Save failed: ${err.message}`, 'error');
                });
            },
            onDelete: (id, storagePath) => this._deleteCandidate(id, storagePath),
            onRetryScore: (candidateId, promptId) => this._retryOneScore(candidateId, promptId),
        });

        // Project Config tab
        this.projectConfig = new ProjectConfigPanel({
            projectStore: this.projectStore,
            promptStore: this.promptStore,
            candidateStore: this.candidateStore,
            analysisQueue: this.analysisQueue,
            statusBar: this.toolbar.statusBar,
            onPromptsChanged: (selected) => {
                this.candidateTable.setSelectedPrompts(selected);
            },
            onImportComplete: (count) => {
                this.toolbar.statusBar.log(`Imported ${count} resume(s)`, 'success');
            },
        });
        this._tabPanels.config = this.projectConfig.element;
        this.tabView.addTab('config', 'Project', this._tabPanels.config);

        // Candidates tab
        this._tabPanels.candidates = this.candidateTable.element;
        this.tabView.addTab('candidates', 'Candidates', this._tabPanels.candidates);

        // Criteria tab
        this.promptEditor = new PromptEditor({
            promptStore: this.promptStore,
            userEmail: this._user.email,
            statusBar: this.toolbar.statusBar,
            onInvalidateScores: (promptId) => this._invalidateScores(promptId),
            onAddToProject: (promptId) => this._addPromptToProject(promptId),
        });
        this._tabPanels.criteria = this.promptEditor.element;
        this.tabView.addTab('criteria', 'Criteria', this._tabPanels.criteria);

        // Email Templates tab
        this.emailTemplateEditor = new EmailTemplateEditor({
            templateStore: this.emailTemplateStore,
            candidateStore: this.candidateStore,
            emailStore: this.emailStore,
            projectStore: this.projectStore,
            db: this.db,
            statusBar: this.toolbar.statusBar,
            userEmail: this._user.email,
            activeProjectId: () => this._activeProjectId,
        });
        this._tabPanels.emails = this.emailTemplateEditor.element;
        this.tabView.addTab('emails', 'Emails', this._tabPanels.emails);

        // Admin tab (admin only)
        if (this._userRole === 'admin') {
            this.userManager = new UserManager({
                db: this.db,
                auth: this.auth,
                statusBar: this.toolbar.statusBar,
                currentUserEmail: this._user.email,
            });
            this._tabPanels.admin = this.userManager.element;
            this.tabView.addTab('admin', 'Users', this._tabPanels.admin);
            this.userManager.init();
        }

        // How-To tab (last — help reference)
        this.howToPanel = new HowToPanel();
        this._tabPanels.howto = this.howToPanel.element;
        this.tabView.addTab('howto', 'Help', this._tabPanels.howto);

        // Project Manager (modal)
        this.projectManager = new ProjectManager(
            document.getElementById('projectManagerModal'),
            {
                projectStore: this.projectStore,
                onSelect: (id) => this._selectProject(id),
            }
        );

        this.toolbar.showButtonsForTab('dashboard');
        this._updateCounts();
    }

    // ========== PROJECT MANAGEMENT ==========

    _initProjectFromUrl() {
        const hashId = this._getProjectIdFromHash();
        if (hashId) {
            const openFromHash = () => {
                const project = this.projectStore.getById(hashId);
                if (project && !this._activeProjectId) {
                    this._selectProject(hashId);
                } else if (!project && this.projectStore.getAll().length > 0 && !this._activeProjectId) {
                    this._selectProject(this.projectStore.getAll()[0].id);
                }
            };
            openFromHash();
            this.projectStore.onChange(openFromHash);
        } else {
            const autoOpen = () => {
                const projects = this.projectStore.getAll();
                if (projects.length > 0 && !this._activeProjectId) {
                    this._selectProject(projects[0].id);
                }
            };
            autoOpen();
            this.projectStore.onChange(autoOpen);
        }

        window.addEventListener('hashchange', () => {
            const id = this._getProjectIdFromHash();
            if (id && id !== this._activeProjectId) {
                this._selectProject(id);
            }
        });
    }

    _getProjectIdFromHash() {
        const match = window.location.hash.match(/project=([^&]+)/);
        return match ? match[1] : null;
    }

    _selectProject(projectId) {
        this._activeProjectId = projectId;
        this._prevCandidateMap = new Map();
        const project = this.projectStore.getById(projectId);

        const newHash = `#project=${projectId}`;
        if (window.location.hash !== newHash) {
            history.replaceState(null, '', newHash);
        }

        this.toolbar.setProjectName(project?.name || '');

        // Set project on dashboard BEFORE subscribing to candidates,
        // so when onChange fires the dashboard already has the projectId
        if (this.dashboard) {
            this.dashboard.setProject(projectId);
        }

        this.candidateStore.subscribeProject(projectId);
        this.emailStore.subscribeProject(projectId);
        this.analysisQueue.subscribeProgress(projectId);
        this.candidateTable.setSelectedPrompts(project?.selectedPrompts || []);
        this.projectConfig.setProject(projectId);

        if (this.emailTemplateEditor?.setProjectId) {
            this.emailTemplateEditor.setProjectId(projectId);
        }

        this.toolbar.statusBar.log(`Opened project: ${project?.name || projectId}`, 'info');
    }

    // ========== TOOLBAR ACTIONS ==========

    _handleToolbarAction(id) {
        switch (id) {
            case 'openProject':
                this.projectManager.show();
                break;
            case 'extractAll':
                this._extractAll();
                break;
            case 'runAnalysis':
                this._runAnalysis();
                break;
            case 'retryFailed':
                this._retryFailed();
                break;
            case 'hideRejected': {
                this._hideRejected = !this._hideRejected;
                this.candidateTable.setHideRejected(this._hideRejected);
                const btn = this.toolbar.getButton('hideRejected');
                if (btn) {
                    btn.innerHTML = getIcon(this._hideRejected ? 'eye' : 'eyeOff');
                    btn.title = this._hideRejected ? 'Show Rejected' : 'Hide Rejected';
                }
                this.toolbar.statusBar.log(
                    this._hideRejected ? 'Hiding rejected candidates' : 'Showing all candidates', 'info');
                this._updateCounts();
                break;
            }
            case 'toggleColumns':
                this._toggleColumnMenu();
                break;
            case 'deleteSelected':
                this._deleteRejected();
                break;
        }
    }

    async _extractAll() {
        if (!this._activeProjectId) {
            this.toolbar.statusBar.log('Open a project first', 'warning');
            return;
        }
        const candidates = this.candidateStore.getAll();
        const extractCount = await this.analysisQueue.createExtractionJob(candidates, this._activeProjectId);
        const needMd = candidates.filter(c => c.extractionStatus === 'complete' && !c.resumeMarkdown);
        let mdCount = 0;
        if (needMd.length > 0) {
            mdCount = await this.analysisQueue.createConvertMdJob(needMd.map(c => c.id), this._activeProjectId);
        }

        if (extractCount === 0 && mdCount === 0) {
            this.toolbar.statusBar.log('All candidates already extracted and converted', 'info');
        } else {
            const parts = [];
            if (extractCount > 0) parts.push(`${extractCount} extraction(s)`);
            if (mdCount > 0) parts.push(`${mdCount} markdown conversion(s)`);
            this.toolbar.statusBar.log(`Started ${parts.join(' + ')}`, 'success');
        }
    }

    async _runAnalysis() {
        if (!this._activeProjectId) {
            this.toolbar.statusBar.log('Open a project first', 'warning');
            return;
        }
        const project = this.projectStore.getById(this._activeProjectId);
        const selectedPrompts = project?.selectedPrompts || [];
        if (selectedPrompts.length === 0) {
            this.toolbar.statusBar.log('No evaluation criteria selected', 'warning');
            return;
        }

        const candidates = this.candidateStore.getAll();
        const totalQueued = await this.analysisQueue.createScoringJobs(
            candidates, this._activeProjectId, selectedPrompts);

        if (totalQueued === 0) {
            this.toolbar.statusBar.log('All candidates already scored', 'info');
        } else {
            this.toolbar.statusBar.log(`Started scoring for ${totalQueued} candidate(s)`, 'success');
        }
    }

    async _deleteRejected() {
        const rejected = this.candidateStore.getAll().filter(c => c.rejected);
        if (rejected.length === 0) {
            this.toolbar.statusBar.log('No rejected candidates to delete', 'info');
            return;
        }
        if (!confirm(`Delete ${rejected.length} rejected candidate(s)?`)) return;
        for (const c of rejected) {
            await this.candidateStore.remove(c.id);
        }
        this.toolbar.statusBar.log(`Deleted ${rejected.length} candidate(s)`, 'info');
    }

    async _invalidateScores(promptId) {
        if (!this._activeProjectId) return;
        const candidates = this.candidateStore.getAll();
        const toRescore = [];
        for (const c of candidates) {
            if (c.scores?.[promptId]) {
                await this.candidateStore.update(c.id, {
                    [`scores.${promptId}`]: firebase.firestore.FieldValue.delete(),
                }).catch(() => {});
                if (c.extractionStatus === 'complete') toRescore.push(c.id);
            }
        }

        if (toRescore.length > 0) {
            const project = this.projectStore.getById(this._activeProjectId);
            const selected = project?.selectedPrompts || [];
            if (selected.includes(promptId)) {
                await this.db.collection('resume-jobs').add({
                    type: 'score',
                    projectId: this._activeProjectId,
                    candidateIds: toRescore,
                    promptId,
                    status: 'pending',
                    progress: { total: toRescore.length, complete: 0, failed: 0, currentName: '' },
                    errors: [],
                    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                });
                this.toolbar.statusBar.log(
                    `Invalidated ${toRescore.length} score(s) — re-scoring started`, 'success');
            } else {
                this.toolbar.statusBar.log(
                    `Invalidated ${toRescore.length} score(s)`, 'info');
            }
        }
    }

    async _addPromptToProject(promptId) {
        if (!this._activeProjectId) {
            this.toolbar.statusBar.log('Open a project first', 'warning');
            return;
        }
        const project = this.projectStore.getById(this._activeProjectId);
        const selected = [...(project?.selectedPrompts || [])];
        if (!selected.includes(promptId)) {
            selected.push(promptId);
            await this.projectStore.update(this._activeProjectId, { selectedPrompts: selected });
            this.candidateTable.setSelectedPrompts(selected);
            this.toolbar.statusBar.log('Criterion added to project', 'success');
        }
    }

    async _retryOneScore(candidateId, promptId) {
        await this.candidateStore.update(candidateId, {
            [`scores.${promptId}`]: firebase.firestore.FieldValue.delete(),
        }).catch(() => {});

        await this.db.collection('resume-jobs').add({
            type: 'score',
            projectId: this._activeProjectId,
            candidateIds: [candidateId],
            promptId,
            status: 'pending',
            progress: { total: 1, complete: 0, failed: 0, currentName: '' },
            errors: [],
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

        this.toolbar.statusBar.log('Retrying score for candidate', 'info');
    }

    async _deleteCandidate(id, storagePath) {
        try {
            await this.candidateStore.remove(id);
            if (storagePath) {
                try { await firebase.storage().ref(storagePath).delete(); } catch (e) { /* ignore */ }
            }
            this.toolbar.statusBar.log('Candidate deleted', 'info');
        } catch (err) {
            this.toolbar.statusBar.log(`Delete failed: ${err.message}`, 'error');
        }
    }

    _toggleColumnMenu() {
        const existing = document.querySelector('.rm-column-menu');
        if (existing) { existing.remove(); return; }

        const menu = this.candidateTable.buildColumnMenu();
        const btn = this.toolbar.getButton('toggleColumns');
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        menu.style.position = 'fixed';
        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.right = `${window.innerWidth - rect.right}px`;
        document.body.appendChild(menu);

        const close = (e) => {
            if (!menu.contains(e.target) && e.target !== btn) {
                menu.remove();
                document.removeEventListener('click', close);
            }
        };
        setTimeout(() => document.addEventListener('click', close), 0);
    }

    // ========== TAB CHANGES ==========

    _onTabChange(tabId) {
        this.toolbar.showButtonsForTab(tabId);
    }

    // ========== DATA CHANGE HANDLERS ==========

    _onProjectsChanged() {
        if (this._activeProjectId) {
            const project = this.projectStore.getById(this._activeProjectId);
            if (project) {
                this.toolbar.setProjectName(project.name);
                this.candidateTable.setSelectedPrompts(project.selectedPrompts || []);
            }
            this.projectConfig.setProject(this._activeProjectId);
        }
    }

    _onCandidatesChanged(candidates) {
        this.candidateTable.setCandidates(candidates);
        this._updateCounts();

        try {
            if (this._prevCandidateMap && this._prevCandidateMap.size > 0) {
                for (const c of candidates) {
                    const prev = this._prevCandidateMap.get(c.id);
                    if (!prev) continue;

                    if (prev.extractionStatus !== 'complete' && c.extractionStatus === 'complete') {
                        this.toolbar.statusBar.log(`Extracted: ${c.name || c.fileName}`, 'success');
                    }
                    if (prev.extractionStatus !== 'error' && c.extractionStatus === 'error') {
                        this.toolbar.statusBar.log(
                            `Extraction failed: ${c.fileName} — ${c.extractionError || 'unknown'}`, 'error');
                    }

                    const scores = c.scores || {};
                    const prevScores = prev.scores || {};
                    for (const [pid, scoreData] of Object.entries(scores)) {
                        const prevScore = prevScores[pid];
                        if (scoreData?.status === 'complete' && prevScore?.status !== 'complete') {
                            const promptName = this.promptStore.getById(pid)?.name || pid;
                            this.toolbar.statusBar.log(
                                `Scored: ${c.name || c.fileName} — ${promptName}: ${scoreData.score}/10`, 'success');
                        }
                        if (scoreData?.status === 'error' && prevScore?.status !== 'error') {
                            const promptName = this.promptStore.getById(pid)?.name || pid;
                            this.toolbar.statusBar.log(
                                `Score failed: ${c.name || c.fileName} — ${promptName}`, 'error');
                        }
                    }
                }
            }
        } catch (err) {
            console.error('Diff tracking error:', err);
        }

        this._prevCandidateMap = new Map(candidates.map(c => [c.id, {
            extractionStatus: c.extractionStatus,
            scores: c.scores ? JSON.parse(JSON.stringify(c.scores)) : {},
        }]));
    }

    _onJobsChanged(jobs) {
        this._latestJobs = jobs;
        if (!jobs || jobs.length === 0) {
            this.toolbar.statusBar.setStatus('');
            return;
        }

        for (const job of jobs) {
            const logLines = job.log || [];
            const prevKey = `_jobLog_${job.id}`;
            const prevCount = this[prevKey] || 0;
            if (logLines.length > prevCount) {
                for (let i = prevCount; i < logLines.length; i++) {
                    const line = logLines[i];
                    const type = line.includes('✓') ? 'success' : line.includes('✗') ? 'error' : 'info';
                    this.toolbar.statusBar.log(line, type);
                }
            }
            this[prevKey] = logLines.length;
        }

        const running = jobs.find(j => j.status === 'running');
        const pending = jobs.find(j => j.status === 'pending');
        const active = running || pending;

        if (running) {
            const updatedAt = running.updatedAt?.toDate?.() || running.updatedAt;
            if (updatedAt && (Date.now() - new Date(updatedAt).getTime()) > 10 * 60 * 1000) {
                this.toolbar.statusBar.setStatus(
                    `Job appears stalled (no update for 10+ min) — ${running.progress?.complete || 0}/${running.progress?.total || '?'} done`);
                return;
            }
        }

        if (active) {
            const p = active.progress || {};
            const pct = p.total > 0 ? Math.round((p.complete / p.total) * 100) : 0;
            const type = active.type === 'extract' ? 'Extracting' : 'Scoring';
            const current = p.currentName ? ` · ${p.currentName}` : '';
            this.toolbar.statusBar.setStatus(
                `${type}: ${p.complete}/${p.total} (${pct}%)${p.failed ? ` · ${p.failed} failed` : ''}${current}`);
        } else {
            const recent = jobs[0];
            const p = recent.progress || {};
            if (recent.status === 'complete' && p.failed === 0) {
                this.toolbar.statusBar.setStatus(`Done: ${p.complete}/${p.total} complete`);
            } else if (recent.status === 'complete' || recent.status === 'failed') {
                this.toolbar.statusBar.setStatus(
                    `Done: ${p.complete}/${p.total} complete · ${p.failed} failed — click Retry Failed`);
            } else if (recent.status === 'continued') {
                this.toolbar.statusBar.setStatus('Job continuing in next batch...');
            }
        }
    }

    async _retryFailed() {
        if (!this._latestJobs || this._latestJobs.length === 0) {
            this.toolbar.statusBar.log('No jobs to retry', 'info');
            return;
        }
        let retried = 0;
        for (const job of this._latestJobs) {
            if ((job.status === 'complete' || job.status === 'failed') &&
                job.errors && job.errors.length > 0) {
                retried += await this.analysisQueue.createRetryJob(job);
            }
        }
        if (retried === 0) {
            this.toolbar.statusBar.log('No failed candidates to retry', 'info');
        } else {
            this.toolbar.statusBar.log(`Retrying ${retried} failed candidate(s)`, 'success');
        }
    }

    _onPromptsChanged(prompts) {
        this.promptEditor.setPrompts(prompts);
        if (this._activeProjectId) {
            const project = this.projectStore.getById(this._activeProjectId);
            this.candidateTable.setSelectedPrompts(project?.selectedPrompts || []);
        }
        this.projectConfig.render();
    }

    _updateCounts() {
        const all = this.candidateStore.getAll();
        const visible = this.candidateTable.getVisibleCount();
        this.toolbar.setCandidateCount(visible, all.length);
    }

    // ========== CLEANUP ==========

    _dispose() {
        if (this.projectStore) this.projectStore.dispose();
        if (this.candidateStore) this.candidateStore.dispose();
        if (this.promptStore) this.promptStore.dispose();
        if (this.emailTemplateStore) this.emailTemplateStore.dispose();
        if (this.emailStore) this.emailStore.dispose();
        if (this.analysisQueue) this.analysisQueue.disposeProgress();
        if (this.dashboard) this.dashboard.dispose();
        if (this.userManager) this.userManager.dispose();
    }
}
